import React, { useEffect, useState } from 'react';
import { Button, Card, Col, Form, Offcanvas, Row, Spinner } from 'react-bootstrap-5';

import LocationInput from './filters/inputs/location_input';
import PropertyTypeInput from './filters/inputs/property_type_input';
import { connect, useDispatch } from 'react-redux';
import { formatWithCommas } from '../../../../../helpers/formatters/number_formats';
import Filter from './filters/filter';
import { addGeoJsons, clearBounds, setFilterAttribute } from '../../store/actions/criteria';
import { clearUrlParam } from '../helpers/url_helper';
import { fetchGeoJSONFromNominatimAsync } from '../helpers/map_helpers';
import type { Criteria } from 'bundles/connect/types';

const FIRST_SEARCH_URL_PARAM = 'first_search';

const SELECTABLE_FILTERS = [
  'minLikelyToSellRank',
  'minLikelyToRefiRank',
  'minTransactionDate',
  'minLoanMaturityDate',
  'maxLoanMaturityDate'
];

type FirstSearchOffcanvasProps = {
  clearBounds: () => void;
  criteria: Criteria;
  loading: boolean;
  nominatimUrl: string;
  regions: {
    county: string;
    state: string;
    [key: string]: any;
  }[];
  setFilterAttribute: (key: string, value: any) => void;
  show: boolean;
  total?: number | null;
}

function FirstSearchOffcanvas(
  {
    clearBounds,
    criteria,
    loading,
    nominatimUrl,
    regions = [],
    setFilterAttribute,
    show,
    total
  }: FirstSearchOffcanvasProps
) {
  const [showOffCanvas, setShowOffcanvas] = useState(show);
  const dispatch = useDispatch();

  const getPlaceIdWithAutocomplete = (query) => {
    return new Promise((resolve) => {
      const service = new google.maps.places.AutocompleteService();
      service.getPlacePredictions(
        { input: query, componentRestrictions: { country: 'us' } },
        (predictions, status) => {
          if (status === google.maps.places.PlacesServiceStatus.OK && predictions && predictions.length > 0) {
            // Prefer administrative_area_level_2 (county) over other types
            const countyPrediction = predictions
              .find(prediction => prediction.types.includes('administrative_area_level_2'));
            resolve(countyPrediction || predictions[0]);
          } else {
            resolve(null);
          }
        }
      );
    });
  };

  const fetchCountiesGeoJSON = async (countyNames) => {
    const predictionPromises = countyNames.map(async (countyName) => {
      try {
        const prediction = await getPlaceIdWithAutocomplete(countyName);
        if (!prediction) return null;
        return { countyName, prediction };
      } catch (error) {
        return null;
      }
    });

    const predictionResults = await Promise.all(predictionPromises);
    const validPredictions = predictionResults.filter(Boolean);

    const geoJsonPromises = validPredictions.map(({ prediction }) => {
      const detail = {
        placeId: prediction.place_id,
        address: prediction.description,
        types: prediction.types,
      };
      return fetchGeoJSONFromNominatimAsync(nominatimUrl, detail);
    });

    const geoJsonResults = await Promise.all(geoJsonPromises);
    const validGeoJsons = geoJsonResults.filter(result => result?.geoJson);

    dispatch<any>(addGeoJsons(validGeoJsons));
  };


  useEffect(() => {
    if (!showOffCanvas) return;

    clearBounds();
    fetchCountiesGeoJSON(regions.map(region => `${region.county} County, ${region.state}`));
  }, []);

  const handleCloseOffcanvas = () => {
    setShowOffcanvas(false);
    clearUrlParam(FIRST_SEARCH_URL_PARAM);
  };

  const handleLikelyRefiSelect = () => {
    const filtersToClear = SELECTABLE_FILTERS.filter(filter => filter !== 'minLikelyToRefiRank');
    filtersToClear.map((filterKey) => {
      setFilterAttribute(filterKey, null);
    });

    setFilterAttribute('minLikelyToRefiRank', 1);
  };

  const handleLikelySellSelect = () => {
    const filtersToClear = SELECTABLE_FILTERS.filter(filter => filter !== 'minLikelyToSellRank');
    filtersToClear.map((filterKey) => {
      setFilterAttribute(filterKey, null);
    });

    setFilterAttribute('minLikelyToSellRank', 1);
  };

  const handleLoanMatureSelect = () => {
    const filtersToClear = SELECTABLE_FILTERS.filter(filter => filter !== 'minLoanMaturityDate' && filter !== 'maxLoanMaturityDate');
    filtersToClear.map((filterKey) => {
      setFilterAttribute(filterKey, null);
    });

    const today = new Date();
    const sixMonthsFromNow = new Date();
    sixMonthsFromNow.setMonth(sixMonthsFromNow.getMonth() + 6);

    const todayFormatted = today.toLocaleDateString('en-CA');
    const sixMonthsFromNowFormatted = sixMonthsFromNow.toLocaleDateString('en-CA');

    setFilterAttribute('minLoanMaturityDate', todayFormatted);
    setFilterAttribute('maxLoanMaturityDate', sixMonthsFromNowFormatted);
  };

  const handleLongOwnerSelect = () => {
    const filtersToClear = SELECTABLE_FILTERS.filter(filter => filter !== 'minTransactionDate');
    filtersToClear.map((filterKey) => {
      setFilterAttribute(filterKey, null);
    });

    const tenYearsAgo = new Date();
    tenYearsAgo.setFullYear(tenYearsAgo.getFullYear() - 10);
    const dateString = tenYearsAgo.toLocaleDateString('en-CA');
    setFilterAttribute('minTransactionDate', dateString);
  };

  const offCanvasBody = (total === undefined || total === null) ? (
    <Offcanvas.Body className="d-flex flex-column align-items-center justify-content-center h-100">
      <Spinner animation="border" variant="secondary" />
    </Offcanvas.Body>
  ) : (
    <>
      <Offcanvas.Body className="d-flex flex-column">
        <div className="d-flex flex-column gap-3">
          <h1 className="d-inline-block text-gradient text-nowrap fw-bolder mb-0" style={{ fontSize: '57px' }}>
            Your first prospects.
          </h1>
          <div className="mb-3">
            Time to build your first search!
            We'll get you started with some commonly used filters to target your prospecting.
            Then you can take off the training wheels.
          </div>
          <div className="d-flex border rounded bg-light p-4 gap-2 align-items-center justify-content-between" style={{ height: '78px' }}>
            <div className="text-muted">
              Properties Found
            </div>
            {loading ? (
              <Spinner animation="border" variant="secondary" />
            ) : (
              <h3 className="mb-0 fw-bolder">
                {formatWithCommas(total)}
              </h3>
            )}
          </div>

          <Filter title="Location">
            <div style={{ minHeight: '40px', position: 'relative' }}>
              <LocationInput />
            </div>
          </Filter>

          <Filter className="border rounded overflow-auto property-type-input" title="Property Type">
            <PropertyTypeInput />
          </Filter>


          <div className="fw-bolder">
            Optional filter presets
          </div>
          <Row className="g-3">
            <Col xs={6}>
              <Card className="clickable" onClick={handleLikelySellSelect}>
                <Card.Body className="d-flex gap-2 p-3">
                  <Form.Check
                    checked={!!criteria.minLikelyToSellRank}
                    id="likely-sell-check"
                    name="filter-preset"
                    type="radio"
                    onChange={e => e.stopPropagation()}
                  />
                  <div>
                    <Form.Label className="fw-bold mb-0" htmlFor="likely-sell-check">
                      Likely To Sell
                    </Form.Label>
                    <div className="text-muted" style={{ fontSize: '12px' }}>
                      Properties with a high likelihood of selling within the next 12 months.
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col xs={6}>
              <Card className="clickable" onClick={handleLikelyRefiSelect}>
                <Card.Body className="d-flex gap-2 p-3">
                  <Form.Check
                    checked={!!criteria.minLikelyToRefiRank}
                    id="likely-refi-check"
                    name="filter-preset"
                    type="radio"
                    onChange={e => e.stopPropagation()}
                  />
                  <div>
                    <Form.Label className="fw-bold mb-0" htmlFor="likely-refi-check">
                      Likely To Refi
                    </Form.Label>
                    <div className="text-muted" style={{ fontSize: '12px' }}>
                      Properties with a high likelihood of refinancing within the next 12 months.
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col xs={6}>
              <Card className="clickable" onClick={handleLongOwnerSelect}>
                <Card.Body className="d-flex gap-2 p-3">
                  <Form.Check
                    checked={!!criteria.minTransactionDate}
                    id="long-term-owner-check"
                    name="filter-preset"
                    type="radio"
                    onChange={e => e.stopPropagation()}
                  />
                  <div>
                    <Form.Label className="fw-bold mb-0" htmlFor="long-term-owner-check">
                      Long-Term Ownership
                    </Form.Label>
                    <div className="text-muted" style={{ fontSize: '12px' }}>
                      Properties that were last sold more than 10 years ago.
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col xs={6}>
              <Card className="clickable" onClick={handleLoanMatureSelect}>
                <Card.Body className="d-flex gap-2 p-3">
                  <Form.Check
                    checked={!!criteria.minLoanMaturityDate && !!criteria.maxLoanMaturityDate}
                    id="loan-maturing-check"
                    name="filter-preset"
                    type="radio"
                    onChange={e => e.stopPropagation()}
                  />
                  <div>
                    <Form.Label className="fw-bold mb-0" htmlFor="loan-maturing-check">
                      Loan Maturing Soon
                    </Form.Label>
                    <div className="text-muted" style={{ fontSize: '12px' }}>
                      Properties with a loan maturity date in the next 6 months.
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
          <div style={{ fontSize: '12px' }}>
            You can modify or add more filters later.
          </div>
        </div>

      </Offcanvas.Body>
      <div className="d-flex justify-content-end border-top p-2">
        <Button variant="secondary" onClick={handleCloseOffcanvas}>
          View Results
        </Button>
      </div>
    </>
  );

  return (
    <Offcanvas
      className="first-search-offcanvas"
      data-bs-backdrop="false"
      placement="start"
      show={showOffCanvas}
      onHide={handleCloseOffcanvas}
    >
      {offCanvasBody}
    </Offcanvas>
  );
}

const mapStateToProps = ({
  criteria,
  results: {
    totalOffMarketResults
  },
  ui: {
    loading
  }
}) => {
  return {
    criteria,
    loading,
    total: totalOffMarketResults
  };
};
export default connect(
  mapStateToProps,
  { clearBounds, setFilterAttribute }
)(FirstSearchOffcanvas);
